import React, { useRef, useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { WindowData } from '../types';

interface WindowProps {
  window: WindowData;
  onClose: (id: string) => void;
  onMinimize: (id: string) => void;
  onMaximize: (id: string) => void;
  onFocus: (id: string) => void;
  children: React.ReactNode;
}

const Window: React.FC<WindowProps> = ({
  window: windowData,
  onClose,
  onMinimize,
  onMaximize,
  onFocus,
  children,
}) => {
  const windowRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: windowData.width, height: windowData.height });
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    if (windowData.maximized) {
      setSize({ width: windowData.width, height: windowData.height });
    }
  }, [windowData.maximized, windowData.width, windowData.height]);

  const handleResizeStart = (e: React.MouseEvent) => {
    if (!windowData.resizable || windowData.maximized) return;

    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = size.width;
    const startHeight = size.height;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(200, startWidth + e.clientX - startX);
      const newHeight = Math.max(100, startHeight + e.clientY - startY);
      setSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleDoubleClickTitle = () => {
    onMaximize(windowData.id);
  };

  if (windowData.minimized) {
    return null;
  }

  return (
    <Draggable
      handle=".window-title-bar"
      position={windowData.maximized ? { x: 0, y: 0 } : undefined}
      defaultPosition={!windowData.maximized ? { x: windowData.x, y: windowData.y } : undefined}
      disabled={windowData.maximized}
    >
      <div
        ref={windowRef}
        className={`window-container ${windowData.maximized ? 'maximized' : ''}`}
        style={{
          position: windowData.maximized ? 'fixed' : 'relative',
          width: windowData.maximized ? '100%' : `${size.width}px`,
          height: windowData.maximized ? 'calc(100% - 32px)' : `${size.height}px`,
          zIndex: windowData.zIndex,
          pointerEvents: 'auto',
        }}
        onMouseDown={() => onFocus(windowData.id)}
        onTouchStart={() => onFocus(windowData.id)}
      >
        <div className="window window-style" style={{ width: '100%', height: '100%', margin: 0 }}>
          <div
            className="title-bar window-title-bar"
            onDoubleClick={handleDoubleClickTitle}
          >
            <div className="title-bar-text window-title">
              {windowData.icon && <span>{windowData.icon}</span>}
              {windowData.title}
            </div>
            <div className="title-bar-controls window-controls">
              <button
                aria-label="Minimize"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMinimize(windowData.id);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMinimize(windowData.id);
                }}
              >
                <span style={{ fontSize: '8px' }}>_</span>
              </button>
              <button
                aria-label="Maximize"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMaximize(windowData.id);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMaximize(windowData.id);
                }}
              >
                <span style={{ fontSize: '8px' }}>□</span>
              </button>
              <button
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose(windowData.id);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose(windowData.id);
                }}
              >
                <span style={{ fontSize: '8px' }}>✕</span>
              </button>
            </div>
          </div>
          <div className="window-body window-content" style={{ margin: 0, padding: 0, height: 'calc(100% - 20px)' }}>
            {children}
          </div>
          {windowData.resizable !== false && !windowData.maximized && (
            <div
              className="window-resize-handle"
              onMouseDown={handleResizeStart}
            />
          )}
        </div>
      </div>
    </Draggable>
  );
};

export default Window;
